import {
  BookPackageOptionProps,
  CatalogProduct,
  PlanInformation,
  ProductFamilies,
  RenewInterval,
} from "./types";
import { offerProductStrings } from "./strings";
import { uniqueId } from 'lodash';

const ITEM_PRICE_PERIODS: Record<string, RenewInterval> = {
  monthly: 'monthly',
  yearly: 'yearly',
  annual: 'yearly',
};

const INSTALLMENTS_PATTERN = /installment/i;

const hasText = (value?: string | null): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Reads the billing period from the last `-` segment of a Chargebee
 * itemPriceId (`{itemHandle}-USD-{Period}`, Period = Monthly/Yearly/Annual).
 * Returns null for any other period (e.g. `Every28days`, `Every-2-months`).
 */
export const getRenewIntervalFromItemPriceId = (
  itemPriceId?: string | null,
): RenewInterval | null => {
  if (!hasText(itemPriceId)) return null;

  const segments = itemPriceId.trim().split('-');
  const period = segments[segments.length - 1].toLowerCase();

  return ITEM_PRICE_PERIODS[period] ?? null;
};

/**
 * Resolves how often a catalog product renews, for the customer-facing plan
 * text. Chargebee (itemPriceId) is the billing system of record, so it wins;
 * the legacy Chargify handle is only used when the product has no
 * itemPriceId. Returns null when the period cannot be expressed as
 * monthly/yearly (unknown suffix, installment plans, or no billing data), so
 * callers never print a made-up "Renews monthly".
 */
export const getRenewInterval = (product: {
  itemPriceId?: string | null;
  chargifyProductHandle?: string | null;
  title?: string | null;
}): RenewInterval | null => {
  const { itemPriceId, chargifyProductHandle, title } = product ?? {};

  if (INSTALLMENTS_PATTERN.test(itemPriceId ?? '') || INSTALLMENTS_PATTERN.test(title ?? '')) {
    return null;
  }

  if (hasText(itemPriceId)) return getRenewIntervalFromItemPriceId(itemPriceId);

  if (hasText(chargifyProductHandle)) {
    return chargifyProductHandle.includes('annual') ? 'yearly' : 'monthly';
  }

  return null;
};

/** Trials are created as Chargify price points, so they need a Chargify handle. */
export const productSupportsTrial = (product: {
  chargifyProductHandle?: string | null;
}): boolean => hasText(product?.chargifyProductHandle);

/** Finds a selectable product option by its catalog `_id`. */
export const findProductOption = (
  productsOptions: BookPackageOptionProps[] | undefined,
  productId?: string | null,
): BookPackageOptionProps | undefined => {
  if (!productId) return undefined;

  return productsOptions?.find(
    (option) => !option.notAllowed && option.productData && option.key === productId,
  );
};

export const formatProductFamilies = (
  productsFamilies: ProductFamilies | undefined | null,
) => {
  const productsOptions = Object.entries(productsFamilies ?? {}).reduce((acc, [k, v]) => {
    if (!Array.isArray(v)) return acc;

    const family = k;

    acc.push({
      id: uniqueId(),
      key: uniqueId(),
      label: (
        <span className="text-xs text-neutral-500 font-semibold uppercase">
          {family}
        </span>
      ),
      value: family,
      notAllowed: true,
    } as BookPackageOptionProps);

    v.forEach((catalogProduct: CatalogProduct) => {
      if (!catalogProduct?.isProductCatalogAllowed) return;

      const {
        _id,
        chargifyProductHandle,
        itemPriceId,
        title,
        type,
        product,
        value,
      } = catalogProduct;
      const renewInterval = getRenewInterval({ itemPriceId, chargifyProductHandle, title });

      acc.push({
        id: _id,
        label: (
          <div>
            <p>{title}</p>
            <p className="text-neutral-400">{type}</p>
          </div>
        ),
        value: _id,
        productData: {
          title,
          type,
          value,
          renewInterval,
          itemPriceId,
          chargifyProductHandle,
          trialSupported: productSupportsTrial({ chargifyProductHandle }),
        },
        notAllowed: false,
        disabledHint: renewInterval
          ? undefined
          : offerProductStrings.billingPeriodNotSupported,
        // MOD catalog products have no `product` field, only a title.
        selectedLabel: product || title,
        key: _id,
      });
    });
    return acc;
  }, [] as BookPackageOptionProps[]);

  return productsOptions;
};

const isRenewInterval = (value?: string | null): value is RenewInterval =>
  value === 'monthly' || value === 'yearly';

/**
 * Builds the checkout "My Subscription Plan" block. When the renew interval
 * is unknown it never guesses one: it keeps the plan text already stored on
 * the offer (if any), otherwise it lists no billing terms.
 */
export const buildPlanInformation = ({
  productName,
  productValueInCents,
  productRenewInterval,
  offerTrialEnabled,
  offerTrialPrice,
  offerTrialDuration,
  storedPlanInformation,
}: {
  productName?: string;
  productValueInCents?: number | string;
  productRenewInterval?: string | null;
  offerTrialEnabled?: boolean;
  offerTrialPrice?: string;
  offerTrialDuration?: string;
  storedPlanInformation?: PlanInformation | null;
}): PlanInformation => {
  const listTitle = `${productName ?? ''} ${offerTrialEnabled && Number(offerTrialDuration) > 0 ? `($${offerTrialPrice} trial)` : ''}`.trim();

  if (!isRenewInterval(productRenewInterval)) {
    return storedPlanInformation ?? { listTitle, listItems: [] };
  }

  const planChargeFrequency = `$${productValueInCents}/${productRenewInterval.replace('ly', '')}`;

  return {
    listTitle,
    listItems: [
      offerTrialEnabled ? `${offerTrialPrice} today for the first ${offerTrialDuration} days` : `${planChargeFrequency} starting today`,
      offerTrialEnabled ? `After ${offerTrialDuration} days, renews at ${planChargeFrequency}` : `Renews ${productRenewInterval}`,
    ],
  };
};

/** Error code DS answers (HTTP 400) for a trial offer on a Chargebee-only product. */
export const OFFER_TRIAL_NOT_SUPPORTED_FOR_CHARGEBEE_PRODUCT =
  'OFFER_TRIAL_NOT_SUPPORTED_FOR_CHARGEBEE_PRODUCT';

/** Picks the toast text for a failed offer save, keeping `fallback` for unknown errors. */
export const getOfferSaveErrorMessage = (error: unknown, fallback: string): string => {
  const code = (error as { response?: { data?: { code?: string } } })?.response?.data?.code;

  return code === OFFER_TRIAL_NOT_SUPPORTED_FOR_CHARGEBEE_PRODUCT
    ? offerProductStrings.trialNotSupported
    : fallback;
};
